import { Fragment } from 'preact'

import type {
  PolicyContext,
  SiteConfig,
} from '../../../../models/site-configs/siteConfigs'

import type { CookieConsentTranslator } from '#src/assets/lib/i18n.ts'
import { Heading } from '#src/assets/ui/shared/components/Heading.tsx'
import { Paragraph } from '#src/assets/ui/shared/components/Paragraph.tsx'

export function PolicySectionCompanyInfo({
  t,
  locale,
  siteConfig,
}: {
  t: CookieConsentTranslator
  locale: string
  siteConfig: SiteConfig
}) {
  const { policyContext } = siteConfig

  const companyDetails = [
    policyContext['companyName'],
    policyContext['companyCountry'],
  ].filter(line => line !== undefined)

  return (
    <Fragment>
      <Heading level={3}>{t('policyWhoWeAreTitle')}</Heading>
      <Paragraph>{t('policyCompanyInfoIntro')}</Paragraph>
      <CompanyDetails companyDetails={companyDetails} />
      <CompanyRegistrationNumber policyContext={policyContext} t={t} />
      <ContactLine policyContext={policyContext} t={t} />
      <EffectiveDate policyContext={policyContext} locale={locale} t={t} />
    </Fragment>
  )
}

function CompanyDetails({ companyDetails }: { companyDetails: string[] }) {
  if (companyDetails.length === 0) {
    return null
  }

  return (
    <Paragraph>
      {companyDetails.map((line, index) => (
        <span key={line}>
          {index > 0 ? <br /> : null}
          {line}
        </span>
      ))}
    </Paragraph>
  )
}

function CompanyRegistrationNumber({
  policyContext,
  t,
}: {
  policyContext: PolicyContext
  t: CookieConsentTranslator
}) {
  const companyRegistration = policyContext.companyRegistrationNumber
  if (!companyRegistration) {
    return null
  }

  return (
    <Paragraph>
      {t('policyCompanyRegistration', {
        registrationNumber: companyRegistration,
      })}
    </Paragraph>
  )
}

function ContactLine({
  policyContext,
  t,
}: {
  policyContext: PolicyContext
  t: CookieConsentTranslator
}) {
  const { contactEmail } = policyContext

  if (!contactEmail) {
    return null
  }

  return (
    <Paragraph>
      {t('policyContactEmailLine', { email: contactEmail })}
    </Paragraph>
  )
}

function EffectiveDate({
  policyContext,
  locale,
  t,
}: {
  policyContext: PolicyContext
  locale: string
  t: CookieConsentTranslator
}) {
  const effectiveDate = policyContext.effectiveDate ?? new Date()
  const formattedEffectiveDate = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(effectiveDate))

  return (
    <Paragraph>
      {t('policyEffectiveDate', { effectiveDate: formattedEffectiveDate })}
    </Paragraph>
  )
}
